import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

interface HeaderProps {
  onMenuClick: () => void
}

export default function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstRender = useRef(true)

  // Sync search input with URL params when they change externally
  useEffect(() => {
    const urlSearchQuery = searchParams.get('search') || ''
    if (urlSearchQuery !== searchQuery) {
      setSearchQuery(urlSearchQuery)
    }
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search function
  const performSearch = useCallback((query: string) => {
    // Preserve existing tags when searching
    const currentParams = new URLSearchParams(window.location.search)
    const tagsParam = currentParams.get('tags')

    if (query.trim()) {
      const searchUrl = tagsParam
        ? `/notes?search=${encodeURIComponent(query.trim())}&tags=${tagsParam}`
        : `/notes?search=${encodeURIComponent(query.trim())}`
      navigate(searchUrl)
    } else {
      // If on notes page with search, clear search but keep tags
      if (window.location.pathname === '/notes' && searchParams.get('search')) {
        const clearUrl = tagsParam ? `/notes?tags=${tagsParam}` : '/notes'
        navigate(clearUrl)
      }
    }
  }, [navigate, searchParams])

  // Debounce search on input change
  useEffect(() => {
    // Skip debounced search on first render
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new debounced search
    debounceTimerRef.current = setTimeout(() => {
      performSearch(searchQuery)
    }, 300) // 300ms debounce

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [searchQuery, performSearch])

  // Handle form submit (immediate search)
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // Clear debounce timer and search immediately
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    performSearch(searchQuery)
  }

  // Clear search
  const handleClear = () => {
    setSearchQuery('')
    // Preserve tags when clearing search
    if (window.location.pathname === '/notes') {
      const currentParams = new URLSearchParams(window.location.search)
      const tagsParam = currentParams.get('tags')
      if (tagsParam) {
        navigate(`/notes?tags=${tagsParam}`)
      } else {
        navigate('/notes')
      }
    }
  }

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-gray-200" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4 lg:px-6">
        {/* Mobile menu button */}
        <button
          className="lg:hidden p-2 sm:p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
          onClick={onMenuClick}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="flex-1 max-w-xl mx-2 sm:mx-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="notes-search"
              className="w-full pl-9 sm:pl-10 pr-8 sm:pr-10 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
            />
            <svg
              className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </form>

        {/* Right side buttons */}
        <div className="flex items-center space-x-1 sm:space-x-2">
          {/* New Note Button */}
          <Link
            to="/notes/new"
            data-testid="header-new-note"
            className="hidden sm:flex items-center px-3 sm:px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors font-medium text-sm min-h-[40px]"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden md:inline">New Note</span>
            <span className="md:hidden">New</span>
          </Link>

          {/* Mobile New Note Button */}
          <Link
            to="/notes/new"
            data-testid="header-new-note-mobile"
            className="sm:hidden p-2.5 text-white bg-primary hover:bg-secondary rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </Link>

          {/* Notifications Button (placeholder) - hidden on very small screens */}
          <button className="hidden xs:flex p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg min-w-[44px] min-h-[44px] items-center justify-center touch-manipulation relative">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  )
}
